import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import {
  LayoutDashboard, Bot, Wallet, Clock, User, LogOut, Menu, X, ChevronDown,
  Sun, Moon, Grid3X3, TrendingUp, ArrowDownToLine, ArrowUpFromLine, BarChart3,
  Cpu, AlertTriangle, Layers, Home
} from 'lucide-react';
import { MetaTradifyLogo } from '@/components/MetaTradifyLogo';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import NotificationBell from '@/components/NotificationBell';

const navGroups = [
  {
    label: 'Trading',
    items: [
      { path: '/dashboard', label: 'Overview', icon: LayoutDashboard },
      { path: '/trade', label: 'Trade', icon: TrendingUp },
      { path: '/positions', label: 'Positions', icon: BarChart3 },
      { path: '/robo-trading', label: 'Robo Trading', icon: Cpu },
      { path: '/services', label: 'Services', icon: Grid3X3 },
      { path: '/ai-assistant', label: 'AI Assistant', icon: Bot },
    ],
  },
  {
    label: 'Finance',
    items: [
      // { path: '/wallet', label: 'Wallet', icon: Wallet },
      { path: '/deposit', label: 'Deposit', icon: ArrowDownToLine, realOnly: true },
      { path: '/withdraw', label: 'Withdraw', icon: ArrowUpFromLine, realOnly: true },
      { path: '/leverage', label: 'Leverage Fund', icon: Layers, realOnly: true },
      { path: '/history', label: 'History', icon: Clock },
    ],
  },
  {
    label: 'Account',
    items: [
      { path: '/profile', label: 'Profile', icon: User },
    ],
  },
];

export default function DashboardLayout({ children }) {
  const { user, logout, refreshUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => { refreshUser(); }, 5000);
    return () => clearInterval(interval);
  }, [refreshUser]);

  const isDemo = user?.account_type === 'demo';
  const filteredGroups = navGroups.map(g => ({
    ...g,
    items: isDemo ? g.items.filter(i => !i.realOnly) : g.items,
  })).filter(g => g.items.length > 0);

  const allItems = filteredGroups.flatMap(g => g.items);
  const currentItem = allItems.find(i => i.path === location.pathname);

  const formatCurrency = (val) => {
    if (val === undefined || val === null) return '$0.00';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
  };

  return (
    <div className="min-h-screen bg-[var(--bg-page)] text-[var(--text-primary)]" data-testid="dashboard-layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Single Sidebar */}
      <aside
        className={`fixed z-50 flex flex-col transition-transform duration-300
          inset-y-0 left-0 w-[260px] bg-[var(--bg-sidebar)] border-r border-[var(--border-default)]
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-[110%] lg:translate-x-0'}`}
      >
        <div className="px-5 pt-5 pb-4 border-b border-[var(--border-subtle)] flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
              <MetaTradifyLogo size={22} />
            </div>
            <span className="text-[17px] font-bold text-[var(--text-primary)] tracking-tight">metaTradify</span>
          </Link>
          <button className="lg:hidden text-[var(--text-secondary)]" onClick={() => setSidebarOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {isDemo && (
          <div className="mx-3 mt-3 flex items-center gap-1.5 px-2.5 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30" data-testid="demo-badge-sidebar">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
            <span className="text-[10px] font-semibold tracking-wide text-amber-600 dark:text-amber-400 uppercase">Demo · 24h expiry</span>
          </div>
        )}

        <nav className="flex-1 px-3 py-4 space-y-4 overflow-y-auto">
          {filteredGroups.map((group) => (
            <div key={group.label} className="space-y-0.5">
              <p className="px-3 pb-1.5 text-[11px] font-semibold text-[var(--text-tertiary)] uppercase tracking-[0.12em]">{group.label}</p>
              {group.items.map((item) => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-[15px] font-medium transition-colors ${
                      isActive
                        ? 'bg-[var(--accent-soft)] text-[var(--accent-text)] font-semibold'
                        : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--hover-bg)]'
                    }`}
                  >
                    <Icon className="w-[18px] h-[18px] shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="px-3 pb-4 pt-3 border-t border-[var(--border-subtle)] space-y-1">
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle-btn"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-[15px] font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--hover-bg)] w-full transition-colors"
          >
            {theme === 'dark' ? <Sun className="w-[18px] h-[18px] text-amber-400" /> : <Moon className="w-[18px] h-[18px]" />}
            {theme === 'dark' ? 'Light mode' : 'Dark mode'}
          </button>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-[15px] font-medium text-[var(--text-secondary)] hover:text-red-500 hover:bg-red-500/10 w-full transition-colors"
          >
            <LogOut className="w-[18px] h-[18px]" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="lg:pl-[260px] flex flex-col min-h-screen">
        <header className="sticky top-0 z-30 glass border-b border-[var(--border-default)]">
          <div className="flex items-center justify-between px-4 lg:px-8 h-16 gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <button
                className="lg:hidden p-2 rounded-lg hover:bg-[var(--hover-bg)] text-[var(--text-secondary)]"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                data-testid="mobile-menu-btn"
              >
                {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
              <div className="hidden md:flex items-center gap-2 text-[15px]">
                <Home className="w-4 h-4 text-[var(--text-tertiary)]" />
                <span className="text-[var(--text-tertiary)]">/</span>
                <span className="text-[var(--text-primary)] font-semibold">{currentItem?.label || 'Dashboard'}</span>
              </div>
            </div>

            <div className="hidden xl:flex items-center gap-5">
              <div className="text-right">
                <p className="text-[10px] font-medium text-[var(--text-tertiary)] uppercase tracking-wider">Balance</p>
                <p className="font-mono text-sm font-semibold text-[var(--text-primary)] tabular-nums" data-testid="header-balance">
                  {formatCurrency(user?.wallet_balance)}
                </p>
              </div>
              <div className="w-px h-9 bg-[var(--border-default)]" />
              <div className="text-right">
                <p className="text-[10px] font-medium text-[var(--text-tertiary)] uppercase tracking-wider">P/L</p>
                <p className={`font-mono text-sm font-semibold tabular-nums ${(user?.total_pnl || 0) >= 0 ? 'text-emerald-500' : 'text-red-500'}`} data-testid="header-pnl">
                  {(user?.total_pnl || 0) >= 0 ? '+' : ''}{formatCurrency(user?.total_pnl || 0)}
                </p>
              </div>
              <div className="w-px h-9 bg-[var(--border-default)]" />
              <div className="text-right">
                <p className="text-[10px] font-medium text-[var(--text-tertiary)] uppercase tracking-wider">Positions</p>
                <p className="font-mono text-sm font-semibold text-[var(--text-primary)] tabular-nums" data-testid="header-positions">
                  {user?.open_positions_count || 0}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {isDemo && (
                <div className="hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-500/10 border border-amber-500/30" data-testid="demo-badge-header">
                  <AlertTriangle className="w-3 h-3 text-amber-500" />
                  <span className="text-[10px] font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wide">Demo</span>
                </div>
              )}
              <NotificationBell />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-[var(--hover-bg)] hover:bg-[var(--bg-muted)] border border-[var(--border-default)] transition-colors" data-testid="user-menu-btn">
                    {user?.profile_picture ? (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${user.profile_picture}`} alt="" className="w-7 h-7 rounded-full object-cover" />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                        <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">{user?.name?.[0]?.toUpperCase()}</span>
                      </div>
                    )}
                    <span className="text-sm text-[var(--text-primary)] hidden sm:block font-medium">{user?.name}</span>
                    <ChevronDown className="w-3.5 h-3.5 text-[var(--text-tertiary)]" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[var(--bg-surface)] border-[var(--border-default)] min-w-[180px] rounded-xl shadow-lg">
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="text-[var(--text-secondary)] focus:bg-[var(--hover-bg)] focus:text-[var(--text-primary)] cursor-pointer rounded-lg">
                    <User className="w-4 h-4 mr-2" /> Profile
                  </DropdownMenuItem>
                  {/* <DropdownMenuItem onClick={() => navigate('/wallet')} className="text-[var(--text-secondary)] focus:bg-[var(--hover-bg)] focus:text-[var(--text-primary)] cursor-pointer rounded-lg">
                    <Wallet className="w-4 h-4 mr-2" /> Wallet
                  </DropdownMenuItem> */}
                  <DropdownMenuItem onClick={logout} className="text-red-500 focus:bg-red-500/10 focus:text-red-500 cursor-pointer rounded-lg">
                    <LogOut className="w-4 h-4 mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 md:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
